import {
  PointOfInterest,
  PointOfInterestsCountByTypeQuery,
  usePointOfInterestsCountByTypeQuery,
  usePointOfInterestsQuery,
} from '@/__generated__/graphql';
import * as analysisState from '@/components/Analysis/state';
import { useSelectedAssemblyTemplateId } from '@/components/Analysis/hooks/useSelectedAssemblyTemplateId';
import { TablePagination } from '@/components/shared/TablePagination';
import { defaultPaginationSize, ALL_POIS_TAB_LABEL, LocationTag } from '@/constants';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { useInsightsPageParams } from '@/hooks';
import { Box, Stack, Typography } from '@mui/material';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { memo, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { VariableSizeList as List, ListChildComponentProps } from 'react-window';
import { Container } from './styles';
import { abyssColors } from '@/theme/colors';
import { feedbackContainerStyles, pointOfInterestTabsStyles } from '../styles';
import { SummaryCard, SummaryCardSkeleton } from './SummaryCard';
import { BasicTabs } from '@/components/shared/BasicTabs';
import { toKebabCase, upperSnakeCase, getPoiTemplateKey } from '@/utils';
import { useMappedFilters } from '@/hooks/useMappedFilters';
import { FilterAndSearchPanel } from '@/components/shared/FilterAndSearchPanel';
import { SelectedValues } from '@/components/shared/FilterAndSearchPanel/FilterAndSearchPanel';
import { useCustomFilters } from '../../../../hooks/useCustomFilters';
import { useDebounce } from '@/hooks/useDebounce';
import { FeedbackCircle } from '@/components/shared/FeedbackCircle';
import { EmptyStateIcon } from '@/components/shared/EmptyStateIcon';
import { poiState } from '@/components/Analysis/modules/pointOfInterest';
import { useLiveSyncAssemblyPois } from '@/components/Analysis/shared/hooks/useLiveSyncAssemblyPois';
import { PointOfInterestsSorting } from './PoiSorting/PointOfInterestsSorting';
import * as state from '@/components/Analysis/state/atoms';
import { ActionHeader } from '../ActionHeader';

type Sorting = {
  sortBy: string;
  sortDirection: number;
};

// Custom comparison to prevent re-renders when unrelated POIs update
const areRowsEqual = (prevProps: ListChildComponentProps, nextProps: ListChildComponentProps) => {
  // Safety check for undefined data
  if (!prevProps.data?.pointOfInterests || !nextProps.data?.pointOfInterests) return false;

  const prevPoi = prevProps.data.pointOfInterests[prevProps.index];
  const nextPoi = nextProps.data.pointOfInterests[nextProps.index];

  // If POI IDs are different, definitely re-render
  if (prevPoi?.id !== nextPoi?.id) return false;

  // If same POI, check if templateFieldData changed (for review status/action updates)
  if (prevPoi?.templateFieldData !== nextPoi?.templateFieldData) return false;

  // Check other props that might change
  return (
    prevProps.index === nextProps.index &&
    prevProps.style === nextProps.style &&
    prevPoi?.checked === nextPoi?.checked
  );
};

const Row = memo(({ index, style, data }: ListChildComponentProps) => {
  // Safety check for undefined data
  if (!data?.pointOfInterests?.[index]) return null;

  return (
    <div style={style}>
      <SummaryCard data={data.pointOfInterests[index]} poiIndex={index} />
    </div>
  );
}, areRowsEqual);

export const PointsOfInterest = () => {
  const location = useLocation();
  const { setReviewPoiTab } = useInsightsPageParams();
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [pointOfInterests, setPointOfInterests] = useAtom(state.allReviewPointsOfInterest);
  const [containerHeight, setContainerHeight] = useState(0);
  const [itemSize, setItemSize] = useState(0);
  const [page, setPage] = useState(0);
  const listRef = useRef<List>(null);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [totalPointsOfInterest, setTotalPointsOfInterest] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const structureId = useGetStructureId();
  const selectedAssemblyId = useAtomValue(analysisState.selectedAssemblyId);
  const allPointOfInterestTemplates = useAtomValue(analysisState.allPointOfInterestTemplates);
  const [selectedFilterValues, setSelectedFilterValues] = useAtom(state.selectedReviewPoiFilters);
  const [paintRegionIds, setPaintRegionIds] = useAtom(state.selectedReviewPaintRegionIds);
  const [workpackIds, setWorkpackIds] = useAtom(state.selectedReviewWorkpackIds);
  const [searchTerm, setSearchTerm] = useAtom(state.reviewPoiSearchTerm);
  const debouncedSearchTerm = useDebounce(searchTerm, 700);
  const [sorting, setSorting] = useState<Sorting>({
    sortBy: 'createdAt',
    sortDirection: 1,
  });
  const [poiCounts, setPoiCounts] = useState<Array<{ type: string; count: number }>>([]);
  const unitSystem = useAtomValue(analysisState.unitSystem);
  const selectedAssemblyTemplateId = useSelectedAssemblyTemplateId();
  const setAllPOI = useSetAtom(poiState.structurePois);
  const setSelectedReviewPoiTypeId = useSetAtom(state.selectedReviewPoiTypeId);
  const setSelectedReviewPointsOfInterest = useSetAtom(state.selectedReviewPointsOfInterest);
  const setIsAllReviewPointsOfInterestSelected = useSetAtom(
    state.isAllReviewPointsOfInterestSelected
  );

  useLiveSyncAssemblyPois();

  const selectedTemplateKey = useMemo(() => {
    if (selectedTab === 0) return undefined;
    const template = allPointOfInterestTemplates[selectedTab - 1];
    return template ? template.key : undefined;
  }, [selectedTab, allPointOfInterestTemplates]);

  const {
    selectedPoiFilters,
    isGoverningFilters,
    isVerifiedFilters,
    accessibilityFilters,
    statusFilters,
    isManuallyCreatedFilters,
    unAssignedPoiFilters,
    mediaAttachmentFilters,
    deckFilters,
  } = useMappedFilters(selectedFilterValues, selectedTemplateKey ?? '');

  const {
    customFiltersConfig,
    customFilterValues,
    handleCustomFilterChange,
    customFilterQueryVars,
    resetCustomFilters,
  } = useCustomFilters();

  const allTemplatesWithAllTab = useMemo(
    () => [
      { name: ALL_POIS_TAB_LABEL, type: '', fields: [], key: 'all' },
      ...allPointOfInterestTemplates,
    ],
    [allPointOfInterestTemplates]
  );

  const tabIndexFromReviewPoiUrl = useCallback(
    (search: string) => {
      const reviewPoiTab = new URLSearchParams(search).get('reviewPoiTab');
      if (!reviewPoiTab) return 0;
      const index = allTemplatesWithAllTab.findIndex(
        (template) => toKebabCase(template.name) === reviewPoiTab
      );
      return index >= 0 ? index : 0;
    },
    [allTemplatesWithAllTab]
  );

  useEffect(() => {
    const index = tabIndexFromReviewPoiUrl(location.search);
    setSelectedTab((previous) => (previous === index ? previous : index));
  }, [location.search, tabIndexFromReviewPoiUrl]);

  const selectedTemplate = allTemplatesWithAllTab[selectedTab];
  const selectedType =
    selectedTemplate?.name === ALL_POIS_TAB_LABEL
      ? undefined
      : upperSnakeCase(getPoiTemplateKey(selectedTemplate));

  const selectedTemplateId = allPointOfInterestTemplates.find(
    (template) => template.name === selectedTemplate?.name
  )?.id;
  useEffect(() => {
    setSelectedReviewPoiTypeId(selectedTemplateId);
  }, [selectedTemplateId, setSelectedReviewPoiTypeId]);

  const queryInput = useMemo(
    () => ({
      structureId,
      assemblyIds: [selectedAssemblyId ?? ''],
      assemblyTemplateId: selectedAssemblyTemplateId,
      paintRegionIds,
      workpackIds,
      selectedPoiFilters,
      isVerified: isVerifiedFilters,
      accessibility: accessibilityFilters,
      status: statusFilters,
      deckIds: deckFilters,
      isManuallyCreated: isManuallyCreatedFilters,
      unAssignedPoi: unAssignedPoiFilters,
      mediaAttachment: mediaAttachmentFilters,
      searchTerm: debouncedSearchTerm,
      isGoverning: isGoverningFilters,
      includeManuallyCreated: true,
      ...customFilterQueryVars,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      structureId,
      selectedAssemblyId,
      selectedAssemblyTemplateId,
      selectedType,
      paintRegionIds,
      workpackIds,
      selectedPoiFilters,
      isVerifiedFilters,
      accessibilityFilters,
      statusFilters,
      deckFilters,
      isManuallyCreatedFilters,
      unAssignedPoiFilters,
      mediaAttachmentFilters,
      debouncedSearchTerm,
      isGoverningFilters,
      customFilterQueryVars,
    ]
  );

  const { data: queryResponse, loading } = usePointOfInterestsQuery({
    variables: {
      input: { ...queryInput, type: selectedType },
      pageInfo: {
        sortBy: sorting.sortBy,
        sortDir: sorting.sortDirection,
        limit: rowsPerPage,
        skip: page * rowsPerPage,
      },
    },
  });

  const onCountsCompleted = (data: PointOfInterestsCountByTypeQuery) => {
    if (data?.pointOfInterestsCountByType) {
      setPoiCounts(
        data.pointOfInterestsCountByType
          .filter(
            (item): item is { type: string; count: number } =>
              item?.type !== null && item?.count !== null
          )
          .map(({ type, count }) => ({ type, count }))
      );
    }
  };

  const { loading: loadingCounts } = usePointOfInterestsCountByTypeQuery({
    skip: !structureId,
    variables: {
      input: queryInput,
    },
    onCompleted: (data) => {
      onCountsCompleted(data);
    },
  });

  const tabs = useMemo(
    () =>
      allTemplatesWithAllTab.map((template, idx) => {
        const typeCount =
          template.name === ALL_POIS_TAB_LABEL
            ? poiCounts.find((poiCount) => poiCount?.type === 'All')?.count || 0
            : poiCounts.find((poiCount) => {
                const type = poiCount?.type?.toLowerCase();
                const name = template?.key?.toLowerCase() ?? template?.name?.toLowerCase();
                const upperSnakeCaseName = upperSnakeCase(template.name).toLowerCase();
                return upperSnakeCaseName === type || name === type;
              })?.count || 0;
        return {
          index: idx,
          label: `${template.name} ${loadingCounts ? '-' : typeCount}`,
          component: null, // dummy, content rendered below
          id: toKebabCase(template.name),
        };
      }),
    [allTemplatesWithAllTab, loadingCounts, poiCounts]
  );

  const feedbackCircleText = useMemo(() => {
    return tabs[selectedTab].label === ALL_POIS_TAB_LABEL
      ? 'No point of interest is available to show'
      : `No ${tabs[selectedTab].label.toLowerCase()} is available to show`;
  }, [selectedTab, tabs]);

  const handleTabChange = useCallback(
    (newTab: number) => {
      setSelectedTab(newTab);
      const newTemplate = allTemplatesWithAllTab[newTab];
      const newTemplateId = allPointOfInterestTemplates.find(
        (template) => template.name === newTemplate?.name
      )?.id;
      setSelectedReviewPoiTypeId(newTab === 0 ? undefined : newTemplateId);
      setReviewPoiTab(
        newTab === 0 ? undefined : toKebabCase(newTemplate?.name ?? '')
      );
      setSelectedReviewPointsOfInterest([]);
      setPage(0);
      setRowsPerPage(5);
      setIsAllReviewPointsOfInterestSelected(false);
      setSelectedFilterValues({});
      setPaintRegionIds([]);
      setWorkpackIds([]);
      setSearchTerm('');
    },
    [
      allTemplatesWithAllTab,
      allPointOfInterestTemplates,
      setSelectedReviewPointsOfInterest,
      setIsAllReviewPointsOfInterestSelected,
      setSelectedFilterValues,
      setPaintRegionIds,
      setWorkpackIds,
      setSearchTerm,
      setSelectedReviewPoiTypeId,
      setReviewPoiTab,
    ]
  );

  const handleSelectedFilterValuesChange = useCallback(
    (selectedValues: SelectedValues) => {
      const filteredSelectedValues = Object.fromEntries(
        Object.entries(selectedValues).filter(([, values]) => values.length > 0)
      );
      setSelectedFilterValues(filteredSelectedValues);
    },
    [setSelectedFilterValues]
  );

  const fieldsWithFilter = useMemo(() => {
    return selectedTemplate?.fields.filter(
      (field) => field.options?.length > 0 && field.displayInQuery
    );
  }, [selectedTemplate]);

  useEffect(() => {
    resetCustomFilters();
  }, [resetCustomFilters, selectedTab, unitSystem]);

  useEffect(() => {
    const data = queryResponse?.pointOfInterests;
    if (data) {
      setPointOfInterests(data as PointOfInterest[]);
      setAllPOI(data as PointOfInterest[]);
      setTotalPointsOfInterest(queryResponse?.pointOfInterestsCount?.count ?? 0);
    }
  }, [
    structureId,
    selectedAssemblyId,
    rowsPerPage,
    page,
    queryResponse,
    setAllPOI,
    setPointOfInterests,
  ]);

  useEffect(() => {
    const currentContainer = containerRef.current;

    const resizeObserver = new ResizeObserver(() => {
      if (currentContainer?.offsetHeight) setContainerHeight(currentContainer.offsetHeight);
      const rootFontSize = getComputedStyle(document.documentElement);
      const parsed = parseFloat(rootFontSize.fontSize);
      setItemSize(20.8 * parsed);
    });

    if (currentContainer) resizeObserver.observe(currentContainer);

    return () => {
      if (currentContainer) resizeObserver.unobserve(currentContainer);
    };
  }, [containerRef]);

  const listHeight = useMemo(() => {
    if (!containerHeight) return 0;
    const actualHeight = itemSize * pointOfInterests.length;
    return Math.min(actualHeight, containerHeight - 60);
  }, [containerHeight, itemSize, pointOfInterests.length]);

  const itemData = useMemo(() => ({ pointOfInterests }), [pointOfInterests]);

  const handleChangePage = (newPage: number): void => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (newRowsPerPage: number): void => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const skeletonHeight = useMemo(() => {
    if (!containerHeight) return 0;
    const actualHeight = itemSize * 3; // 3 skeletons
    return Math.max(actualHeight + 16, listHeight);
  }, [containerHeight, itemSize, listHeight]);

  const renderTabContent = () => (
    <Container position="relative" ref={containerRef} height="100%">
      {loading ? (
        <Stack height={skeletonHeight} gap="1.6rem">
          {Array.from({ length: 3 }, (_, index) => (
            <SummaryCardSkeleton key={index} />
          ))}
        </Stack>
      ) : pointOfInterests.length > 0 ? (
        <>
          <List
            ref={listRef}
            height={listHeight}
            itemCount={pointOfInterests.length}
            width="100%"
            itemSize={() => itemSize}
            itemData={itemData}
            className="custom-scrollbar"
          >
            {Row}
          </List>
          <Stack>
            <TablePagination
              count={totalPointsOfInterest}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[
                { label: '5', value: 5 },
                ...defaultPaginationSize,
                { label: 'All', value: totalPointsOfInterest },
              ]}
            />
          </Stack>
        </>
      ) : (
        <Box sx={feedbackContainerStyles} data-testid="feedback-circle">
          <FeedbackCircle
            text={feedbackCircleText}
            icon={<EmptyStateIcon icon={tabs[selectedTab].label} poiTemplateKey={selectedType} />}
          />
        </Box>
      )}
    </Container>
  );

  return (
    <Container padding="1.4rem" height="100%" id="review-panel-points-of-interest">
      <BasicTabs
        tabs={tabs.map((tab) => ({
          ...tab,
          component: (
            <Container
              position="relative"
              paddingTop="1.6rem"
              sx={{
                scrollBehavior: 'smooth',
              }}
            >
              <Stack direction="row" alignItems="center">
                <FilterAndSearchPanel
                  templateFields={fieldsWithFilter}
                  selectedValues={{ ...selectedFilterValues, ...customFilterValues }}
                  setSelectedValues={setSelectedFilterValues}
                  handleSelectedChange={handleSelectedFilterValuesChange}
                  showPaintRegions
                  searchBar
                  searchTerm={searchTerm}
                  setSearch={setSearchTerm}
                  customFiltersConfig={customFiltersConfig}
                  customFilterValues={customFilterValues}
                  handleCustomFilterChange={handleCustomFilterChange}
                  sx={{ marginBottom: 0 }}
                  location={LocationTag.ReviewPanelPoiList}
                  poiType={selectedType}
                />
                <Box sx={{ marginLeft: '2.4rem' }}>
                  <Typography sx={{ color: abyssColors.primary[400] }}>Sort:</Typography>
                </Box>
                <PointOfInterestsSorting sorting={sorting} setSorting={setSorting} />
              </Stack>
              <ActionHeader
                location={LocationTag.ReviewPanelPoiList}
                poiType={
                  selectedTemplate?.name === ALL_POIS_TAB_LABEL ? undefined : selectedTemplate?.name
                }
                templateId={selectedTemplateId}
              />
              {renderTabContent()}
            </Container>
          ),
        }))}
        selectedTab={selectedTab}
        changeTab={handleTabChange}
        variant="pills"
        tabsStyles={pointOfInterestTabsStyles}
        tabIdPrefix={LocationTag.ReviewPanelPoiType}
      />
    </Container>
  );
};
